import { AxiosError, isAxiosError } from 'axios'
import { z, ZodType } from 'zod'

import { ERROR_MESSAGE } from '@/config/http/http.constants'
import { ApiBusinessDayResponse } from '@/ducks/account/account.schema'

export type IError = {
  store_aquisition_url?: string
  error_flag?: number
  errors?: string
  message: string
  status?: string
  code?: string
  cityBusinessHours?: z.infer<typeof ApiBusinessDayResponse>[]
  /**
   * True when the request failed for a reason that is worth retrying: timeout,
   * connection drop or a 5xx-class response. Set by the RTK Query base query.
   */
  transient?: boolean
}

const TRANSIENT_HTTP_STATUSES = [408, 425, 429, 500, 502, 503, 504]

/**
 * A request killed by the axios timeout or by a network blip surfaces with no
 * response at all — that is the failure mode behind the cold-boot stalls.
 * Cancellations are deliberate (RTK Query aborting an unsubscribed query), so
 * they must never be retried.
 */
export function isTransientFailure(error: unknown): boolean {
  if (!isAxiosError(error)) return false
  if (error.code === 'ERR_CANCELED') return false
  if (!error.response) return true

  return TRANSIENT_HTTP_STATUSES.includes(error.response.status)
}

export const isErrorType = (arg: unknown): arg is IError =>
  !!arg && typeof arg === 'object' && 'message' in arg

export function shapeError(error: unknown): IError {
  if (isAxiosError(error)) {
    const axiosError = error as AxiosError<IError>

    const data = axiosError.response?.data
    const status = axiosError.response?.status
    const code = axiosError.code
    if (typeof data === 'string')
      return { message: data, status: status?.toString(), code }
    if (!data)
      return { message: ERROR_MESSAGE, status: status?.toString(), code }
    if (data.errors)
      return {
        ...data,
        message: data.errors,
        status: (data as IError).status || status?.toString(),
        code
      }
    if (data.message)
      return {
        ...data,
        status: (data as IError).status || status?.toString(),
        code
      }
  }

  if (error instanceof Error) return { message: error.message }

  if (isErrorType(error)) return error

  return { message: 'An unexpected error occurred' }
}

export const responseDataHasError = (data: Record<keyof any, unknown>) => {
  if (!data || typeof data !== 'object') return

  return (
    'error_flag' in data ||
    'errors' in data ||
    (data?.status && data.status === 0)
  )
}

const ENVIRONMENT = import.meta.env.VITE_ENVIRONMENT as string
export function parseResponse<T>(data: any, schema: ZodType<T>): T {
  if (ENVIRONMENT === 'development') {
    return schema.parse(data)
  } else {
    return data
  }
}
